import React from "react";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { ShieldAlert } from "lucide-react";
import Logo from "@/components/Logo";

const STORAGE_PREFIX = "cc_disclaimer_accepted_";

export default function DisclaimerModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    try {
      const v = localStorage.getItem(STORAGE_PREFIX + user.id);
      if (!v) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, [user?.id]);

  if (!user?.id || !open) return null;

  const accept = () => {
    try { localStorage.setItem(STORAGE_PREFIX + user.id, new Date().toISOString()); } catch {}
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 animate-fade-in" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }} data-testid="disclaimer-modal">
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-lg border border-stone-200 overflow-hidden" onClick={(e)=>e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-stone-100 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center shrink-0 border border-amber-200">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-brand-700 mb-1.5"><Logo size={22} withWordmark /></div>
            <h3 className="font-display font-semibold text-stone-900 text-lg">Important — Please read before continuing</h3>
            <p className="text-xs text-stone-500 mt-0.5">Welcome back, {user.full_name?.split(" ")[0] || "user"}.</p>
          </div>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm text-stone-700 max-h-[55vh] overflow-y-auto">
          <p>
            <b>OncoCadence</b> is a <b>scheduling and coordination tool</b> for chemotherapy treatment plans.
            It is intended to assist licensed healthcare professionals and their patients with planning
            and communication, and is <b>not a substitute for professional medical judgement</b>.
          </p>
          <p>
            By continuing, you confirm that:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>You will not enter or share more personal health information than is necessary for treatment coordination.</li>
            <li>Dosing, drug selection and scheduling decisions must always be reviewed by a qualified clinician.</li>
            <li>You will keep your login credentials confidential and sign out when finished on a shared device.</li>
            <li>Emergencies must be handled by calling local emergency services — this app is not for emergency use.</li>
          </ul>
          <p className="text-xs text-stone-500 pt-2 border-t border-stone-100">
            This acknowledgement is recorded for this device. You can re-read this notice anytime from your Profile.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-stone-100 flex justify-end gap-2 bg-stone-50">
          <button onClick={accept} data-testid="disclaimer-accept-btn" className="px-5 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold">
            I understand &amp; accept
          </button>
        </div>
      </div>
    </div>
  );
}
